import { css } from "@emotion/react";
import { useSearchParams } from "next/navigation";
import React from "react";
import { Descendant } from "slate";
import { Global } from "theme-ui";

import { TextBlockProvider } from "@/components/TextBlock/TextBlockContext";
import { TextBlockRenderProvider } from "@/components/TextBlock/TextBlockRenderContext";
import { renderTextBlockStatic } from "@/components/TextBlock/TextBlockStatic/TextBlockStatic";
import { useExperience } from "@/context/ExperienceContext";

export const ContentPage: React.FC<
  React.PropsWithChildren<{
    renderElement: React.ComponentProps<typeof TextBlockRenderProvider>["renderElement"];
    renderLeaf: React.ComponentProps<typeof TextBlockRenderProvider>["renderLeaf"];
    renderTextBlockPopover: () => React.JSX.Element;
    textBlocks: Record<string, Descendant[]> | null;
  }>
> = ({ children, renderElement, renderLeaf, renderTextBlockPopover, textBlocks }) => {
  const { country, environment, language } = useExperience();
  const searchParams = useSearchParams();
  const [renderTextBlock, setRenderTextBlock] = React.useState<{
    renderer: React.ComponentProps<typeof TextBlockRenderProvider>["renderTextBlock"];
  }>({ renderer: renderTextBlockStatic });
  const editing = !!searchParams.get("edit") && environment === "local";

  const handleTextBlockChange = React.useCallback(
    (id: string, contents: Descendant[]) => {
      if (country && language) {
        fetch(`/api/text`, {
          method: "POST",
          body: JSON.stringify({ country, id, contents, language })
        });
      }
    },
    [country, language]
  );

  React.useEffect(() => {
    if (editing) {
      const loadFunction = async () => {
        const { renderTextBlockEditor } = await import(
          "@/components/TextBlock/TextBlockEditor/TextBlockEditor"
        );
        setRenderTextBlock({ renderer: renderTextBlockEditor });
      };

      loadFunction();
    } else if (renderTextBlock.renderer !== renderTextBlockStatic) {
      setRenderTextBlock({ renderer: renderTextBlockStatic });
    }
  }, [editing, renderTextBlock.renderer]);

  return (
    <TextBlockProvider
      textBlocks={textBlocks || {}}
      onTextBlockChange={editing ? handleTextBlockChange : () => {}}
    >
      <TextBlockRenderProvider
        editing={editing}
        renderElement={renderElement}
        renderLeaf={renderLeaf}
        renderTextBlock={renderTextBlock.renderer}
      >
        <Global
          styles={css`
            @font-face {
              font-family: "Knockout";
              font-display: block;
              font-style: normal;
              font-weight: 400;
              src: url(https://static.modernatx.com/fonts/Knockout-Medium.woff2) format("woff2");
            }
            @font-face {
              font-family: "Oswald";
              font-display: block;
              font-style: normal;
              font-weight: 100 1000;
              src:
                url(https://static.modernatx.com/fonts/Oswald.woff2)
                  format("woff2 supports variations"),
                url(https://static.modernatx.com/fonts/Oswald.woff2) format("woff2-variations");
            }
          `}
        />
        {children}
        {environment === "local" && renderTextBlockPopover()}
      </TextBlockRenderProvider>
    </TextBlockProvider>
  );
};
